import { SpatialFeatureClass } from './SpatialFeatureClass';

class SpatialFeature {
  constructor({
    id,
    area_id,
    sports,
    created_at,
    created_by,
    updated_at,
    updated_by,
    archived_at,
    archived_by,
    timestamp,
    geometry,
    class_id,
    class: feature_class,
    parent_id,
    is_public,
    properties,
  }) {
    this.id = id;
    this.areaId = area_id;
    this.sports = sports;
    this.created_at = created_at;
    this.created_by = created_by;
    this.updated_at = updated_at;
    this.updated_by = updated_by;
    this.archived_at = archived_at;
    this.archived_by = archived_by;
    this.timestamp = timestamp;
    this.geometry = geometry;
    this.class_id = class_id;
    this.feature_class = new SpatialFeatureClass(feature_class);
    this.parent_id = parent_id;
    this.is_public = is_public;
    this.properties = properties;
    // relationships
    this.parent = null;
    this._children = {};
  }

  get children() {
    return Object.values(this._children);
  }

  addChild(child) {
    child.parent = this;
    this._children[child.id] = child;
  }

  removeChild(child) {
    delete this._children[child.id];
  }

  getAllAncestors() {
    const ancestors = [];
    let parent = this.parent;
    while (parent) {
      ancestors.push(parent);
      parent = parent.parent;
    }
    return ancestors;
  }

  getAllDescendants() {
    const descendants = [];
    const recurse = (feature) => {
      if (feature) {
        descendants.push(feature);
      }
      feature.children?.forEach(recurse);
    };
    recurse();
    return descendants;
  }

  get allProperties() {
    const allProperties = [];
    const instanceProperties = this.properties || [];
    for (const classProperty of this.feature_class.properties) {
      const instanceProperty = instanceProperties.find((p) => p.key === classProperty.key);
      allProperties.push({
        ...classProperty,
        ...instanceProperty,
      });
    }
    return allProperties;
  }

  get propertyMap() {
    const propertyMap = {};
    const spatialFeatureProperties = this.properties || [];
    for (const property of spatialFeatureProperties) {
      propertyMap[property.key] = property.value;
    }
    return propertyMap;
  }

  get spatialFeatureClassName() {
    return this.feature_class?.name || 'Item';
  }

  get displayName() {
    const spatialFeatureProperties = this.allProperties || [];
    const spatialFeatureNameProperty = spatialFeatureProperties.find((property) => property.key === 'name');
    const spatialFeatureNumberProperty = spatialFeatureProperties.find((property) => property.key === 'number');
    const className = this.feature_class.name || 'Item';

    if (spatialFeatureNameProperty?.value) {
      return spatialFeatureNameProperty.value;
    }
    if (spatialFeatureNumberProperty?.value != null) {
      if (className) {
        return `${this.feature_class.name}: ${spatialFeatureNumberProperty.value}`;
      }
      return spatialFeatureNumberProperty.value;
    }
    return className;
  }

  toObject() {
    const obj = { ...this };
    delete obj.feature_class;
    delete obj._children;
    delete obj.parent;
    obj.properties = this.allProperties.map(p => ({key: p.key, value: p.value}));
    return obj;
  }

  isInstanceOf(spatialFeatureClassSlug) {
    return this.feature_class.hierarchy.includes(spatialFeatureClassSlug);
  }
}

class SpatialFeatureSet {
  constructor() {
    this.spatialFeatures = {};
  }

  add(spatialFeatures) {
    // add all to the index
    spatialFeatures?.forEach((spatialFeature) => {
      this.spatialFeatures[spatialFeature.id] = spatialFeature;
    });
    // set parent relationships
    spatialFeatures?.forEach((spatialFeature) => {
      if (spatialFeature.parent_id) {
        const parent = this.spatialFeatures[spatialFeature.parent_id];
        if (parent) {
          parent.addChild(spatialFeature);
        }
      }
    });
  }

  remove(spatialFeatures) {
    spatialFeatures?.forEach((spatialFeature) => {
      // remove from index
      delete this.spatialFeatures[spatialFeature.id];
      // remove from parent
      if (spatialFeature.parent) {
        spatialFeature.parent.removeChild(spatialFeature);
      }
    });
  }

  // _generateTree(root) {
  //   const tree = root;
  //   for (let child of root.children) {
  //     this._generateTree(child);
  //   }
  //   // tree.children = root.children.map(child => this._generateTree(child));
  //   return tree;
  // }

  generateTrees() {
    const trees = [];
    for (let id in this.spatialFeatures) {
      const spatialFeature = this.spatialFeatures[id];
      if (!spatialFeature.parent) {
        trees.push(spatialFeature);
      }
    }
    return trees;
  }

  getById(id) {
    return this.spatialFeatures[id];
  }

  get count() {
    return Object.keys(this.spatialFeatures).length;
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export { SpatialFeature, SpatialFeatureSet };

